import * as stylex from "@stylexjs/stylex";
import { useLayoutEffect, useRef, useState } from "react";
import { Provider } from "react-aria-components";

import {
  ChevronLeftStrokeIcon,
  ChevronRightStrokeIcon,
  IconButton,
  TabContext,
  TabList,
  Tabs,
  type TabListProps,
  type TabsProps,
  useMediaQuery,
  useMeasure,
  useScroll,
} from "@/libs/serenity-ui";
import { breakpoints } from "@/libs/serenity-ui/constants.stylex";

interface InterestTabs<T extends object> extends TabListProps<T>, Pick<TabsProps, 'onSelectionChange' | 'defaultSelectedKey' | 'selectedKey'> {
  isDisabled?: boolean;
  style?: stylex.StyleXStyles;
}

export function InterestTabs<T extends object>({
  style,
  children,
  isDisabled,
  onSelectionChange,
  defaultSelectedKey,
  selectedKey,
  ...otherProps
}: InterestTabs<T>) {
  const tabsRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDesktop = useMediaQuery(breakpoints.large);
  const [scrollEdges, setScrollEdges] = useState({ atStart: true, atEnd: true });

  const [tabListRef, dimensions] = useMeasure();
  const [scrollState, scroll] = useScroll(tabsRef);

  const isTabListDimensionsMeasured = dimensions.width !== null;
  const shouldCenterTabList = isTabListDimensionsMeasured && dimensions.width! <= 728;

  useLayoutEffect(() => {
    const node = tabsRef.current;
    if (!node) {
      return;
    }

    const updateScrollEdges = () => {
      const atStart = node.scrollLeft <= 0;
      const atEnd = node.scrollLeft + node.clientWidth >= node.scrollWidth - 1;

      setScrollEdges((prev) => (prev.atStart === atStart && prev.atEnd === atEnd ? prev : { atStart, atEnd }));
    };

    updateScrollEdges();

    node.addEventListener("scroll", updateScrollEdges, { passive: true });

    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateScrollEdges) : null;
    resizeObserver?.observe(node);

    return () => {
      node.removeEventListener("scroll", updateScrollEdges);
      resizeObserver?.disconnect();
    };
  }, []);

  const startScrolling = (direction: 'left' | 'right') => {
    stopScrolling();
    scrollIntervalRef.current = setInterval(() => scroll.by({ left: direction === 'left' ? -5 : 5, behavior: 'auto' }), 10);
  };

  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const scrollTo = (direction: 'left' | 'right') => scroll.to({ left: scrollState.x + (direction === 'left' ? -300 : 300), behavior: 'smooth' });

  return (
    <div {...stylex.props(styles.base)}>
      {isDesktop && (
        <IconButton
          aria-label="Scroll left"
          size={32}
          style={[scrollButtonStyles.base, scrollEdges.atStart && styles.hidden]}
          variant="ghost"
          onMouseDown={() => startScrolling('left')}
          onMouseLeave={() => stopScrolling()}
          onMouseUp={() => stopScrolling()}
          onPress={() => scrollTo('left')}>
          <ChevronLeftStrokeIcon />
        </IconButton>)}

      <Tabs
        defaultSelectedKey={defaultSelectedKey}
        isDisabled={isDisabled}
        ref={tabsRef}
        selectedKey={selectedKey}
        style={tabsStyles.base}
        onSelectionChange={onSelectionChange}>
        <Provider values={[[TabContext, { size: 48 }]]}>
          <TabList
            ref={tabListRef}
            {...otherProps}
            style={[!isTabListDimensionsMeasured ? styles.hidden : shouldCenterTabList && tabListStyles.centered, style]}>
            {children}
          </TabList>
        </Provider>
      </Tabs>

      {isDesktop && (
        <IconButton
          aria-label="Scroll right"
          size={32}
          style={[scrollButtonStyles.base, scrollEdges.atEnd && styles.hidden]}
          variant="ghost"
          onMouseDown={() => startScrolling('right')}
          onMouseLeave={() => stopScrolling()}
          onMouseUp={() => stopScrolling()}
          onPress={() => scrollTo('right')}>
          <ChevronRightStrokeIcon />
        </IconButton>)}
    </div>
  );
}

const styles = stylex.create({
  base: {
    alignItems: 'end',
    display: 'flex',
    flexDirection: 'row',
    height: 54,
  },
  hidden: {
    display: 'none',
  },
});

const tabsStyles = stylex.create({
  base: {
    borderBottomStyle: 'none',
    flexBasis: 'auto',
    flexGrow: 1,
    flexShrink: 1,
    overflowX: 'visible',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
  },
});

const tabListStyles = stylex.create({
  centered: {
    marginLeft: 'auto',
    marginRight: 'auto',
    minWidth: 728,
  }
});

const scrollButtonStyles = stylex.create({
  base: {
    alignSelf: 'center',
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0,
    marginLeft: 8,
    marginRight: 16,
    position: 'relative',
  },
});
